import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BASEMAP_ADDON_GIS,
  BASEMAP_ESRI_NAVIGATION_DARK,
  BASEMAP_ESRI_NAVIGATION_LIGHT,
  BASEMAP_ESRI_SATELLITE,
  BASEMAP_GOOGLE_HYBRID,
  BASEMAP_GOOGLE_ROADMAP,
} from '@rsos/constants/basemapAddonNames';
import { ESRI, GOOGLE } from '@rsos/constants/mapProviders';
import { getFlag } from '@rsos/feature-flags';
import useSelectedTheme from '@rsos/hooks/useSelectedTheme';
import {
  clearAllGISServices,
  selectBasemapService,
} from '@rsos/reduxDataStore/src/features/gisServices/gisServicesSlice';
import {
  closeAllCards,
  STATIC_LAYERS_CARD,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { callQueueSelector } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import {
  setBasemap,
  setIsBasemapVisible,
  setMapAddon,
  setMapProvider,
} from '@rsos/sinatra';
import { LIGHT_THEME } from '@rsos/theme/constants';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import checkGISServicesAccess from '@rsos/utils/checkGISServicesAccess';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { exportMapViewState } from '@rsos/utils/mapViewState/exportMapViewState';
import {
  BasemapGap,
  BasemapMenuContainer,
  BasemapMenuPosition,
  BasemapTile,
  BasemapTileText,
  MenuContainer,
} from './BasemapMenu.styles';
import BasemapsAddonsSwitch from './BasemapsAddonsSwitch';
import { trackBasemapClick, trackAddonClick } from './basemapMenuTrackings';
import { addons, defaultBasemaps, noneBasemap } from './basemapsAddonsList';

export const TILE_BASEMAP = 'Map';

export const TILE_SATELLITE = 'Satellite';

const BasemapMenu = () => {
  const dispatch = useDispatch();
  const hasBasemapSublayer = getFlag('basemap_sublayer');

  const hasEsriMapAccess = useSelector(state =>
    checkEsriMapAccess({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const hasGISServiceAccess = useSelector(state =>
    checkGISServicesAccess({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const mapCard = useSelector(state => state?.mapCard?.cards);

  const irpVersion = useSelector(state => state.sinatra.irp.irpVersion);

  const themeName = useSelectedTheme();

  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const basemap = useSelector(state => state.sinatra.ui.basemap);

  const mapAddons = useSelector(state => state.sinatra.ui.mapAddons);

  // The GIS addon in the basemap menu refers to the Esri GIS cached-basemap service.
  const gisAddon = useSelector(selectBasemapService);

  const hasGISAddon = hasGISServiceAccess && gisAddon;

  // If the authority has Esri GIS addon, add the 'none' basemap to the basemaps list.
  const basemaps = useMemo(() => {
    return hasGISAddon ? [...defaultBasemaps, noneBasemap] : defaultBasemaps;
  }, [hasGISAddon]);

  const isSatelliteBasemap = useMemo(() => {
    return (
      basemap === BASEMAP_ESRI_SATELLITE || basemap === BASEMAP_GOOGLE_HYBRID
    );
  }, [basemap]);

  const [isHovered, setIsHovered] = useState(false);

  const getTileText = useMemo(() => {
    return isSatelliteBasemap
      ? isHovered
        ? TILE_BASEMAP
        : TILE_SATELLITE
      : isHovered
        ? TILE_SATELLITE
        : TILE_BASEMAP;
  }, [isHovered, isSatelliteBasemap]);

  const getBasemapName = useMemo(() => {
    if (mapProvider === ESRI) {
      return isSatelliteBasemap
        ? themeName === LIGHT_THEME
          ? BASEMAP_ESRI_NAVIGATION_LIGHT
          : BASEMAP_ESRI_NAVIGATION_DARK
        : BASEMAP_ESRI_SATELLITE;
    } else if (mapProvider === GOOGLE) {
      return basemap === BASEMAP_GOOGLE_ROADMAP
        ? BASEMAP_GOOGLE_HYBRID
        : BASEMAP_GOOGLE_ROADMAP;
    }
  }, [basemap, isSatelliteBasemap, mapProvider, themeName]);

  const selectedCallerID = useSelector(
    state => state?.emergencyCalls?.selectedCallerID,
  );

  const selectedAlertID = useSelector(
    state => state?.incidents?.selectedAlertID,
  );

  const hasAniAliAccess = useSelector(state =>
    checkAniAliAccessSelector({ psaps: state.psaps, sinatra: state.sinatra }),
  );

  const callQueue = useSelector(state =>
    state?.emergencyCalls?.callQueue
      ? callQueueSelector(state.emergencyCalls.callQueue, hasAniAliAccess)
      : {},
  );

  const hasLocation = !!getMostRecentLoc(callQueue?.[selectedCallerID]);

  const isAllView = useMemo(() => {
    return (!selectedCallerID || !hasLocation) && !selectedAlertID;
  }, [hasLocation, selectedAlertID, selectedCallerID]);

  // Used to help determine the proper zoom level when switching map providers.
  const [initialEsriBasemap, setInitialEsriBasemap] = useState(
    mapProvider === ESRI ? basemap : '',
  );

  const handleHover = () => {
    setIsHovered(prev => !prev);
  };

  const handleBasemapClick = useCallback(
    (newMapProvider, newBasemap, isNewBasemapVisible) => {
      dispatch(setMapProvider(newMapProvider));

      dispatch(
        setBasemap(
          isSatelliteBasemap
            ? newMapProvider === ESRI
              ? BASEMAP_ESRI_SATELLITE
              : BASEMAP_GOOGLE_HYBRID
            : newBasemap,
        ),
      );

      dispatch(setIsBasemapVisible(isNewBasemapVisible));

      // If Esri 'none' basemap is selected, ensure the GIS addon is also selected.
      if (
        newMapProvider === ESRI &&
        !isNewBasemapVisible &&
        !mapAddons.includes(BASEMAP_ADDON_GIS)
      ) {
        dispatch(setMapAddon(BASEMAP_ADDON_GIS));
      }

      // Clear GIS layers when switching to Google (they'll be refetched when switching back to ESRI)
      if (newMapProvider === GOOGLE) {
        dispatch(clearAllGISServices());
      }

      if (newMapProvider === ESRI) {
        setInitialEsriBasemap(
          isSatelliteBasemap ? BASEMAP_ESRI_SATELLITE : newBasemap,
        );
      }

      // Export the current map view state (lat, lng, width, height, and zoom) to the window when
      // in all events view.
      if (isAllView) {
        exportMapViewState(initialEsriBasemap);
      }

      // Close the Geocomm indoor maps card when switching to Google map as it is not supported.
      if (mapCard[STATIC_LAYERS_CARD]) {
        dispatch(closeAllCards());
      }

      trackBasemapClick({
        irpVersion,
        mapProvider: newMapProvider,
        basemap: isSatelliteBasemap ? basemap : newBasemap,
        isBasemapVisible: isNewBasemapVisible,
      });
    },
    [
      basemap,
      dispatch,
      initialEsriBasemap,
      irpVersion,
      isAllView,
      isSatelliteBasemap,
      mapAddons,
      mapCard,
    ],
  );

  const handleAddonClick = useCallback(
    addon => {
      dispatch(setMapAddon(addon));

      trackAddonClick({
        irpVersion,
        isEnabled: !mapAddons.includes(BASEMAP_ADDON_GIS),
      });
    },
    [dispatch, irpVersion, mapAddons],
  );

  const handleTileClick = useCallback(() => {
    if (mapProvider === ESRI) {
      dispatch(setBasemap(getBasemapName));
    } else if (mapProvider === GOOGLE) {
      dispatch(setBasemap(getBasemapName));
    }

    dispatch(setIsBasemapVisible(true));

    trackBasemapClick({
      irpVersion,
      mapProvider,
      basemap: getBasemapName,
      isBasemapVisible: true,
    });
  }, [dispatch, getBasemapName, irpVersion, mapProvider]);

  return (
    <BasemapMenuPosition data-name="basemap-menu-position">
      <BasemapMenuContainer
        data-name="basemap-menu-container"
        onMouseEnter={handleHover}
        onMouseLeave={handleHover}
      >
        <BasemapTile
          data-name="basemap-tile"
          isSatelliteBasemap={isSatelliteBasemap}
          onClick={() => handleTileClick()}
          theme={themeName}
        >
          <BasemapTileText data-name="basemap-tile-text">
            {getTileText}
          </BasemapTileText>
        </BasemapTile>

        {hasEsriMapAccess && (
          <>
            {/* 16 pixel white space between the button and the menu. */}
            <BasemapGap data-name="basemap-gap" />

            <MenuContainer data-name="menu-container">
              <BasemapsAddonsSwitch
                basemaps={basemaps}
                data-name="basemaps-switch"
                menuType="basemaps"
                onHandleClick={handleBasemapClick}
                title="Basemaps"
              />
              {/*
               * basemap_sublayer flag will toggle off this button
               * allowing mapLayersControl to handle the basemap's visibility
               */}
              {hasGISAddon && !hasBasemapSublayer && (
                <BasemapsAddonsSwitch
                  addons={addons}
                  data-name="addons-switch"
                  menuType="addons"
                  onHandleClick={handleAddonClick}
                />
              )}
            </MenuContainer>
          </>
        )}
      </BasemapMenuContainer>
    </BasemapMenuPosition>
  );
};

export default BasemapMenu;
